// src/pages/Appli/AppliAdmin/AlbumsPhotos/AlbumsPhotos.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import DOMPurify from "dompurify";
import { FaPlus, FaImages } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import {
  getAllGalleries,
  deleteGallery,
  addGallery,
} from "../../../../services/gallerieServices";

import Button from "../../../../components/Button/Button";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";

import CreateAlbumModal from "./CreateAlbumModal";
import EditAlbumModal from "./EditAlbumModal";
import ManagePhotoModal from "./ManagePhotoModal";

import { LanguageContext } from "../../../../contexts/LanguageContext";

const AlbumsPhotos = () => {
  const [albums, setAlbums] = useState([]);
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isManagePhotoModalOpen, setManagePhotoModalOpen] = useState(false);
  const [currentAlbum, setCurrentAlbum] = useState(null);

  const [isDemoMode, setIsDemoMode] = useState(false);

  const { language } = useContext(LanguageContext);
  const [localLanguage, setLocalLanguage] = useState(language || "fr");

  const getLocalizedKey = (key) =>
    `${key}_${String(localLanguage).toLowerCase()}`;

  const checkUserRole = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role_id === 4) setIsDemoMode(true);
  };

  const guardDemo = () => {
    if (isDemoMode) {
      toast.info("Fonction désactivée en mode Démonstration");
      return true;
    }
    return false;
  };

  const fetchGalleries = async () => {
    try {
      const data = await getAllGalleries();
      setAlbums(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors de la récupération des albums :", error);
      toast.error("Erreur chargement albums.");
    }
  };

  useEffect(() => {
    fetchGalleries();
    checkUserRole();
  }, []);

  const handleCreate = async (newAlbum) => {
    if (guardDemo()) return;
    try {
      await addGallery(newAlbum);
      await fetchGalleries();
      setCreateModalOpen(false);
      toast.success("Album créé ✅");
    } catch (error) {
      console.error("Erreur lors de la création de l'album :", error);
      toast.error("Erreur création album.");
    }
  };

  const handleEdit = (album) => {
    if (guardDemo()) return;
    setCurrentAlbum(album);
    setEditModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (guardDemo()) return;
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet album ?"))
      return;

    try {
      await deleteGallery(id);
      await fetchGalleries();
      toast.success("Album supprimé ✅");
    } catch (error) {
      console.error("Erreur lors de la suppression de l'album :", error);
      toast.error("Erreur suppression album.");
    }
  };

  const handleManagePhotos = (album) => {
    setCurrentAlbum(album);
    setManagePhotoModalOpen(true);
  };

  const safeHtml = (html) => DOMPurify.sanitize(String(html || ""));

  const ui = useMemo(() => {
    const card =
      "rounded-3xl border border-black/10 bg-white/80 shadow-sm backdrop-blur " +
      "dark:bg-white/5 dark:border-white/10";
    const title = "text-gray-900 dark:text-white";
    const muted = "text-gray-600 dark:text-gray-300";
    const chip =
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold " +
      "border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20";
    const btnIcon =
      "w-9 h-9 inline-flex items-center justify-center rounded-2xl " +
      "border border-black/10 dark:border-white/10 " +
      "bg-white/70 dark:bg-black/20 hover:bg-white dark:hover:bg-black/30 transition";
    return { card, title, muted, chip, btnIcon };
  }, []);

  return (
    <div className="min-h-screen">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className={`${ui.card} p-5 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`text-2xl sm:text-3xl font-extrabold ${ui.title}`}>
                Gestion des Albums Photos
              </h1>
              <p className={`mt-1 text-sm ${ui.muted}`}>
                Crée, édite tes albums et gère les photos. (Kanji masqué dans
                l’admin)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Lang */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${ui.muted}`}>
                  Langue
                </span>
                <select
                  value={localLanguage}
                  onChange={(e) => setLocalLanguage(e.target.value)}
                  className="rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white outline-none"
                >
                  <option value="fr">FR</option>
                  <option value="en">EN</option>
                </select>
              </div>

              <Button
                text={
                  <>
                    <FaPlus className="mr-2" /> Créer un album
                  </>
                }
                onClick={() => {
                  if (!guardDemo()) setCreateModalOpen(true);
                }}
                className="gap-2"
              />
            </div>
          </div>
        </div>

        {/* Desktop table */}
        <div className="mt-6 hidden lg:block">
          <div className={`${ui.card} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-black/5 dark:bg-white/5">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Titre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Description
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Photos
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Créé le
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-extrabold uppercase tracking-wider text-gray-700 dark:text-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-black/5 dark:divide-white/10">
                  {albums.map((album) => {
                    const title =
                      album[getLocalizedKey("titre")] || album.titre || "—";
                    const desc =
                      album[getLocalizedKey("description")] ||
                      album.description ||
                      "<p>Description indisponible</p>";

                    const count = Array.isArray(album.photos)
                      ? album.photos.length
                      : 0;
                    const created = album.createdAt
                      ? new Date(album.createdAt).toLocaleDateString("fr-FR")
                      : "—";

                    return (
                      <tr
                        key={album.id}
                        className={
                          (album.is_active
                            ? "bg-transparent"
                            : "bg-black/5 dark:bg-white/5") +
                          " hover:bg-black/5 dark:hover:bg-white/5"
                        }
                      >
                        <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white">
                          {title}
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                          <div
                            className="max-w-[680px] line-clamp-3 prose prose-sm max-w-none dark:prose-invert"
                            dangerouslySetInnerHTML={{ __html: safeHtml(desc) }}
                          />
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span className={ui.chip}>{count}</span>
                        </td>

                        <td className="px-4 py-3 text-center text-sm text-gray-700 dark:text-gray-200">
                          {created}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span className={ui.chip}>
                            {album.is_active ? "Actif" : "Inactif"}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <ButtonEdit onClick={() => handleEdit(album)} />
                            <button
                              type="button"
                              className={`${ui.btnIcon}`}
                              onClick={() => handleManagePhotos(album)}
                              title="Gérer les photos"
                            >
                              <FaImages />
                            </button>
                            <ButtonDelete
                              onClick={() => handleDelete(album.id)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!albums.length && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-10 text-center text-sm text-gray-600 dark:text-gray-300"
                      >
                        Aucun album pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="mt-6 lg:hidden space-y-4">
          {albums.map((album) => {
            const title = album[getLocalizedKey("titre")] || album.titre || "—";
            const desc =
              album[getLocalizedKey("description")] ||
              album.description ||
              "<p>Description indisponible</p>";
            const count = Array.isArray(album.photos) ? album.photos.length : 0;
            const created = album.createdAt
              ? new Date(album.createdAt).toLocaleDateString("fr-FR")
              : "—";

            return (
              <div key={album.id} className={`${ui.card} p-5`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className={`text-lg font-extrabold ${ui.title} truncate`}
                    >
                      {title}
                    </div>
                    <div className={`text-xs ${ui.muted} mt-1`}>
                      <span className={ui.chip}>{count} photos</span>
                      <span className="ml-2">{created}</span>
                      <span className="ml-2">
                        {album.is_active ? "Actif" : "Inactif"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`${ui.btnIcon}`}
                    onClick={() => handleManagePhotos(album)}
                    title="Gérer les photos"
                  >
                    <FaImages />
                  </button>
                </div>

                <div
                  className="mt-3 prose prose-sm max-w-none dark:prose-invert line-clamp-4"
                  dangerouslySetInnerHTML={{ __html: safeHtml(desc) }}
                />

                <div className="mt-4 flex justify-end gap-2">
                  <ButtonEdit onClick={() => handleEdit(album)} />
                  <ButtonDelete onClick={() => handleDelete(album.id)} />
                </div>
              </div>
            );
          })}

          {!albums.length && (
            <div className={`${ui.card} p-6 text-center text-sm ${ui.muted}`}>
              Aucun album pour le moment.
            </div>
          )}
        </div>

        {/* Modales */}
        {isCreateModalOpen && (
          <CreateAlbumModal
            onClose={() => setCreateModalOpen(false)}
            onCreate={handleCreate}
          />
        )}

        {isEditModalOpen && (
          <EditAlbumModal
            album={currentAlbum}
            onClose={() => setEditModalOpen(false)}
            onSave={fetchGalleries}
          />
        )}

        {isManagePhotoModalOpen && (
          <ManagePhotoModal
            album={currentAlbum}
            onClose={() => setManagePhotoModalOpen(false)}
            onUpdated={fetchGalleries}
          />
        )}
      </div>
      <ToastContainer />
    </div>
  );
};

export default AlbumsPhotos;
